import {
  Backdrop,
  Box,
  Grid,
  styled,
  Table,
  Menu,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  MenuItem
} from "@mui/material"
import Header from "components/Header/Header"
import AppPagination from "components/StyledComponents/AppPagination"
import AppButton from "components/StyledComponents/AppButton"
import { createBreadcrumbs } from "helpers/breadCrumbs"
import { notableVipGroupColumns } from "helpers/constant"
import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  getNotableVipGroupsAction,
  getVipGroupDynamicFieldsAction,
  deleteNotableVipGroupAction,
  getNotableGroupTypesAction
} from "redux/superAdmin/actions"
import VipGroupFieldsModal from "./components/VipGroupFieldsModal"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import { formatDate } from "helpers/functions"

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  padding: "12px 24px"
}))

const CustomTable = styled(Table)(({ theme }) => ({
  backgroundColor: theme.palette.background.main,
  borderCollapse: "collapse !important",
  "& .MuiTableCell-root": {
    padding: "12px 24px 12px 0px",
    backgroundColor: theme.palette.background.main,
    borderBottom: "none",
    boxSizing: "border-box",
    "&:last-child": {
      paddingRight: 0
    }
  },
  "& .MuiTableRow-root": {
    borderBottom: `2px solid ${theme.palette.drawer.main}`,
    "&:last-child": {
      borderBottom: "none"
    }
  },
  "& .MuiTableCell-head": {
    verticalAlign: "bottom",
    position: "sticky",
    borderBottom: `2px solid ${theme.palette.drawer.main}`,
    "&:last-child": {
      paddingRight: 0
    }
  }
}))

const NotableVIPGroup = () => {
  const [breadCrumbs, setBreadCrumbs] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [openModal, setOpenModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [anchorEl, setAnchorEl] = useState(null)

  const dispatch = useDispatch()
  const { notableVipGroups, menuFields } = useSelector((store) => store?.superAdmin)
  const { isLoading } = useSelector((store) => store?.app)
  const theme = useTheme()
  const openMenu = Boolean(anchorEl)

  useEffect(() => {
    document.title = "Notable Groups"
    setBreadCrumbs(createBreadcrumbs())
    dispatch(getNotableVipGroupsAction())
    dispatch(getVipGroupDynamicFieldsAction())
    dispatch(getNotableGroupTypesAction())
  }, [])

  const handleAddGroup = () => {
    setIsEdit(false)
    setSelectedGroup(null)
    setOpenModal(true)
  }

  const handleEditGroup = (group) => {
    setIsEdit(true)
    setSelectedGroup(group)
    setOpenModal(true)
    handleMenuClose()
  }

  const handleDeleteGroup = (id) => {
    dispatch(deleteNotableVipGroupAction({ id }))
    handleMenuClose()
  }

  const handleMenuClick = (event, group) => {
    setAnchorEl(event.currentTarget)
    setSelectedGroup(group)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const itemsPerPage = 12
  const totalPages = Math.ceil((notableVipGroups?.length || 0) / itemsPerPage)

  const paginatedList = Array.isArray(notableVipGroups)
    ? notableVipGroups.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    : []

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} className="d-flex justify-content-between align-items-center">
        <Header title="Notable Groups" breadCrumbs={breadCrumbs} />
        <AppButton onClick={handleAddGroup} styles={{ padding: "14px 30px" }}>
          Add VIP Group
        </AppButton>
      </Grid>
      <Grid item xs={12}>
        <TableContainer sx={{ maxHeight: 700 }}>
          <CustomTable>
            <TableHead>
              <TableRow>
                {notableVipGroupColumns?.map((column) => (
                  <TableCell
                    key={column.id}
                    sx={{ minWidth: column.minWidth }}
                    align={column.align}>
                    <Typography variant="body2Regular" color={theme.palette.text.secondary}>
                      {column.label}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedList?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={notableVipGroupColumns?.length} align="center">
                    {isLoading ? "Please wait we are getting the groups..." : "No Data Available"}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedList?.map((row, index) => (
                  <TableRow key={row?.id || index}>
                    <TableCell align="center">
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </TableCell>
                    <TableCell align="left">{row?.name || "-"}</TableCell>
                    <TableCell align="center">{row?.description || "-"}</TableCell>
                    <TableCell align="center">{formatDate(row?.dates) || "-"}</TableCell>
                    <TableCell align="center">
                      <MoreHorizIcon
                        sx={{ cursor: "pointer" }}
                        onClick={(e) => handleMenuClick(e, row)}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </CustomTable>
        </TableContainer>
        {paginatedList?.length > 0 && (
          <Box className="d-flex justify-content-end mt-3">
            <AppPagination
              totalPages={totalPages}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
            />
          </Box>
        )}
        <Backdrop
          sx={{ color: theme.palette.white.main, zIndex: 1300 }}
          open={openMenu}
          onClick={handleMenuClose}>
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={openMenu}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right"
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right"
            }}
            PaperProps={{
              sx: {
                marginTop: "15px",
                backgroundColor: theme.palette.background.default,
                borderRadius: "12px",
                "& .MuiList-root": {
                  backgroundColor: theme.palette.background.default,
                  width: "225px",
                  boxSizing: "border-box",
                  padding: "10px 0"
                }
              }
            }}>
            <StyledMenuItem onClick={() => handleEditGroup(selectedGroup)}>
              <Typography variant="body1Regular">Edit</Typography>
            </StyledMenuItem>
            <StyledMenuItem onClick={() => handleDeleteGroup(selectedGroup?.id)}>
              <Typography sx={{ color: theme.palette.red.main }} variant="body1Regular">
                Delete
              </Typography>
            </StyledMenuItem>
          </Menu>
        </Backdrop>
      </Grid>

      <VipGroupFieldsModal
        open={openModal}
        isEdit={isEdit}
        onClose={() => setOpenModal(false)}
        dynamicFieldsGroup={menuFields}
        groupDetails={selectedGroup}
      />
    </Grid>
  )
}

export default NotableVIPGroup
